import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { rootRoute } from './__root';
import {
  settingsSearchSchema,
  type SettingsSection,
} from '@/features/settings/search-schema';
import { profileKeys } from '@/features/settings/queries';
import { SettingsPendingState } from '@/features/settings/SettingsPendingState';
import { getProfile } from '@/lib/profile';
import { requireAuth } from '@/lib/route-guards';
import { isRouteRoleAllowed } from '@/features/access/role-route-guard';
import { SECTION_ACCESS } from '@/features/settings/section-meta';

/**
 * Sections renamed in place — same route, just a `?section=` rewrite rather
 * than a redirect to a different path. "Team" → "Workspace access",
 * "Organization" → "Company", "Organization Members" → "People".
 */
const RENAMED_SECTIONS: Record<string, SettingsSection> = {
  team: 'access',
  organization: 'company',
  'org-members': 'people',
};

export const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  validateSearch: settingsSearchSchema,
  beforeLoad: (ctx) => {
    requireAuth(ctx);
    // Templates and API keys/Partner integration moved off `/settings` onto
    // their own sidebar entries. An old `?section=templates` or
    // `?section=api-keys` bookmark/link still parses (see
    // `DEPRECATED_SETTINGS_SECTIONS`) so it can land here and redirect,
    // rather than silently falling back to the Profile tab. Mirrors the
    // `beforeLoad` + `throw redirect(...)` shape in `routes/index.tsx`.
    if (ctx.search.section === 'templates') {
      throw redirect({ to: '/templates', replace: true });
    }
    if (ctx.search.section === 'api-keys') {
      throw redirect({ to: '/api-keys', replace: true });
    }
    const renamed = ctx.search.section && RENAMED_SECTIONS[ctx.search.section];
    if (renamed) {
      throw redirect({
        to: '/settings',
        search: { ...ctx.search, section: renamed },
        replace: true,
      });
    }
    // Role-blocked section deep link → the Profile section (every role can use
    // it), not a lock card or an empty panel. Plan locks stay in-page.
    // (Deprecated ids were redirected above, so what's left is a live section.)
    const access = SECTION_ACCESS[ctx.search.section as SettingsSection];
    // Sync for every section without a role requirement, so the redirects above
    // (and Profile, the default) stay synchronous throws.
    if (!access) return;
    return isRouteRoleAllowed(ctx.context.queryClient, access).then((allowed) => {
      if (!allowed) {
        throw redirect({ to: '/settings', search: { section: 'profile' }, replace: true });
      }
    });
  },
  loader: async ({ context: { queryClient } }) => {
    // Prime the profile cache before the component mounts. Every section reads
    // `useProfileQuery` (role gating) and the default `profile` section renders
    // straight from it, so this skips the first-paint loading flash. Swallow the
    // error — the page renders its own loading / error / retry states.
    await queryClient
      .ensureQueryData({ queryKey: profileKeys.current(), queryFn: getProfile })
      .catch(() => undefined);
  },
  // `pendingMs: 0` / `pendingMinMs: 0` — no debounce, no artificial display
  // floor (see `routes/email-accounts.$id.tsx` for the precedent/rationale).
  pendingComponent: SettingsPendingState,
  pendingMs: 0,
  pendingMinMs: 0,
  component: lazyRouteComponent(
    () => import('@/features/settings/SettingsPage'),
    'SettingsPage',
  ),
});
